import React from "react";
import { Shield, Music, DollarSign } from "lucide-react";

/**
 * EditMemberModal - Modal dialog for editing member details, roles, and account type.
 * Used by the AdminDirectoryTab.
 */
const EditMemberModal = ({ isOpen, member, formData, setFormData, onSubmit, onClose }) => {
  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-member-modal">
      <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Edit Member</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black"
            data-testid="edit-member-close-btn"
          >
            &#10005;
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-3 border-2 border-black"
              required
              data-testid="edit-member-name"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 border-2 border-black"
              required
              data-testid="edit-member-email"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full p-3 border-2 border-black"
              data-testid="edit-member-phone"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-1">Mailing Address</label>
            <textarea
              value={formData.mailing_address}
              onChange={(e) => setFormData({ ...formData, mailing_address: e.target.value })}
              className="w-full p-3 border-2 border-black"
              rows={2}
              data-testid="edit-member-address"
            />
          </div>

          {/* Account Roles */}
          <div className="border-t-2 border-black pt-4">
            <label className="block text-sm font-bold mb-3">Account Roles</label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border-2 border-gray-200 hover:border-black cursor-pointer transition-colors" data-testid="edit-member-role-admin">
                <input
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  className="w-5 h-5 accent-red-600"
                />
                <Shield size={18} className="text-red-600" />
                <div>
                  <span className="font-bold text-sm">Admin</span>
                  <p className="text-xs text-gray-500">Full access to manage bookings, finance, and members</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border-2 border-gray-200 hover:border-black cursor-pointer transition-colors" data-testid="edit-member-role-kj">
                <input
                  type="checkbox"
                  checked={formData.is_kj}
                  onChange={(e) => setFormData({ ...formData, is_kj: e.target.checked })}
                  className="w-5 h-5 accent-purple-600"
                />
                <Music size={18} className="text-purple-600" />
                <div>
                  <span className="font-bold text-sm">KJ (Karaoke Jockey)</span>
                  <p className="text-xs text-gray-500">Can be assigned to sessions, access KJ Console</p>
                </div>
              </label>
            </div>
          </div>

          {/* KJ Type — only show when KJ role is checked */}
          {formData.is_kj && (
            <div className="border-2 border-purple-200 bg-purple-50 p-3">
              <label className="block text-sm font-bold mb-2">KJ Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-member-kj-external">
                  <input
                    type="radio"
                    name="kj_type"
                    checked={formData.kj_allowance_eligible}
                    onChange={() => setFormData({ ...formData, kj_allowance_eligible: true })}
                    className="accent-purple-600"
                  />
                  <DollarSign size={14} className="text-green-600" />
                  <span className="text-sm font-bold">External KJ ($35/session)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-member-kj-rn">
                  <input
                    type="radio"
                    name="kj_type"
                    checked={!formData.kj_allowance_eligible}
                    onChange={() => setFormData({ ...formData, kj_allowance_eligible: false })}
                    className="accent-purple-600"
                  />
                  <span className="text-sm font-bold">RN Member (Volunteer)</span>
                </label>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border-2 border-black font-bold hover:bg-gray-100"
              data-testid="edit-member-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 px-4 bg-blue-500 text-white border-2 border-black font-bold hover:bg-blue-600"
              data-testid="edit-member-save-btn"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberModal;
